import API, { Types } from '@/modules/API';
import {
  REPORTING_QUERY_VERSION,
  setDeviceCommonFields,
  setCabinetFields
} from '@/store/modules/Reports';

const STREAM_ITEMS_LIMIT = 999999999;
const STREAM_BATCH_SIZE = 1000;

const SLIM_CABINET_COLUMNS = [
  'id',
  'class_name',
  'sys___active',
  'sys___connected',
  'meta.category',
  'meta.device',
  'cabinet.operation_state'
];

const CABINET_DETAIL_COLUMNS = [
  'id',
  'sys___connected',
  'dmodified_ts',
  'services.config',
  'meta.devices_schedule',
  'dashboard.connection_display',
  'device.connections',
  'device.schedule',
  'device.version.os_release',
  'control',
  'digital_input'
];

const FIXTURE_COUNT_COLUMNS = ['count(*)', 'meta.device.cabinet_id'];
const FIXTURE_BASE_WHERE = "meta.category.category='FIXTURE' AND meta.commission.commissioned=true";

interface ProjectIds {
  companyId: string;
  projectId: string;
  defaultLocation?: any;
}

interface SlimCabinetsParams extends ProjectIds {
  onBatch?: (batch: any[]) => void;
}

interface CabinetDetailsParams extends ProjectIds {
  ids: string[];
}

interface FixtureCountsParams {
  companyId: string;
  projectId: string;
}

export interface FixtureCount {
  total: number;
  connected_active: number;
  retrofit: number;
  retrofit_connected_active: number;
}

/**
 * In-memory snapshot cache for the Cabinet Management page.
 *
 * Keyed by `${companyId}::${projectId}` so switching projects gets its own
 * entry and doesn't bleed cross-project data. TTL is 15 minutes — when the
 * user re-enters the page within that window we skip the network entirely
 * and rebuild the page state from the snapshot. Past TTL we refetch and
 * show the loading spinner.
 *
 * Mutations elsewhere in the app that change cabinets (create / delete /
 * commission / meta.device edits) should call `invalidateCabinetSnapshot`
 * so the next visit refetches.
 */
export const CABINET_CACHE_TTL_MS = 10 * 60 * 1000;

interface CabinetSnapshot {
  cachedAt: number;
  cabinets: any[];
  fixtureCounts: Record<string, FixtureCount>;
  details: Map<string, any>;
}

const snapshotCache = new Map<string, CabinetSnapshot>();
const snapshotKey = (companyId: string, projectId: string) => `${companyId}::${projectId}`;

export function getCabinetSnapshot(companyId: string, projectId: string): CabinetSnapshot | null {
  return snapshotCache.get(snapshotKey(companyId, projectId)) || null;
}

export function isCabinetSnapshotFresh(companyId: string, projectId: string): boolean {
  const entry = snapshotCache.get(snapshotKey(companyId, projectId));
  return !!entry && (Date.now() - entry.cachedAt) < CABINET_CACHE_TTL_MS;
}

export function primeCabinetSnapshot(
  companyId: string,
  projectId: string,
  data: { cabinets: any[]; fixtureCounts: Record<string, FixtureCount> }
) {
  const key = snapshotKey(companyId, projectId);
  const existing = snapshotCache.get(key);
  snapshotCache.set(key, {
    cachedAt: Date.now(),
    cabinets: data.cabinets,
    fixtureCounts: data.fixtureCounts,
    details: (existing && existing.details) || new Map()
  });
}

export function addCabinetDetailsToSnapshot(
  companyId: string,
  projectId: string,
  details: any[]
) {
  const entry = snapshotCache.get(snapshotKey(companyId, projectId));
  if (!entry || !details || !details.length) return;
  details.forEach((d) => { if (d && d.id) entry.details.set(d.id, d); });
}

export function invalidateCabinetSnapshot(companyId?: string, projectId?: string) {
  if (companyId && projectId) snapshotCache.delete(snapshotKey(companyId, projectId));
  else snapshotCache.clear();
}

const reportingPath = (companyId: string, projectId: string) =>
  `/${companyId}/projects/${projectId}/streaming_reporting_query/`;

/**
 * Slim cabinet list — minimal columns to render closed panels and feed
 * OverviewInfo. Streams via onBatch so the page can paint incrementally.
 */
export async function fetchCabinetsSlim({
  companyId,
  projectId,
  defaultLocation,
  onBatch
}: SlimCabinetsParams): Promise<any[]> {
  const body: any = {
    columns: SLIM_CABINET_COLUMNS,
    where: [
      "meta.category.category='HUB'",
      'meta.commission.commissioned=true'
    ],
    items: STREAM_ITEMS_LIMIT,
    version: REPORTING_QUERY_VERSION
  };

  const apiParams: any = {};
  if (onBatch) {
    apiParams.batchSize = STREAM_BATCH_SIZE;
    apiParams.onBatch = (batch: any[]) => {
      batch.forEach((device) => setDeviceCommonFields(device, defaultLocation));
      onBatch(batch);
    };
  }

  const result = await API.streamingPost(
    Types.COMPANIES,
    reportingPath(companyId, projectId),
    body,
    apiParams
  );
  if (!onBatch && Array.isArray(result)) {
    result.forEach((device: any) => setDeviceCommonFields(device, defaultLocation));
  }
  return result || [];
}

/**
 * Full cabinet details — batched fetch for a set of cabinet ids. Returns the
 * per-id details array (resolved in one shot, no streaming).
 */
export async function fetchCabinetsDetails({
  companyId,
  projectId,
  ids
}: CabinetDetailsParams): Promise<any[]> {
  if (!ids || !ids.length) return [];

  const idList = ids.map((id) => `'${id}'`).join(',');

  const body: any = {
    columns: CABINET_DETAIL_COLUMNS,
    where: [`id IN [${idList}]`],
    items: STREAM_ITEMS_LIMIT,
    version: REPORTING_QUERY_VERSION
  };

  const result = await API.streamingPost(
    Types.COMPANIES,
    reportingPath(companyId, projectId),
    body,
    {}
  );
  const list = Array.isArray(result) ? result : [];
  list.forEach((device: any) => setCabinetFields(device));
  return list;
}

/**
 * Project-wide fixtures count per cabinet via two aggregated reporting queries:
 *   1) total commissioned fixtures grouped by meta.device.cabinet_id
 *   2) connected & active commissioned fixtures grouped by meta.device.cabinet_id
 * Returns a map keyed by cabinet_id (human cabinet id) → { total, connected_active }.
 * Replaces the previous per-cabinet row scan.
 */
export async function fetchAllFixtureCounts({
  companyId,
  projectId
}: FixtureCountsParams): Promise<Record<string, FixtureCount>> {
  const buildBody = (extraWhere?: string): any => ({
    columns: FIXTURE_COUNT_COLUMNS,
    where: [extraWhere ? `${FIXTURE_BASE_WHERE} AND ${extraWhere}` : FIXTURE_BASE_WHERE],
    group: 'meta.device.cabinet_id',
    items: STREAM_ITEMS_LIMIT,
    version: REPORTING_QUERY_VERSION
  });

  const path = reportingPath(companyId, projectId);
  // Retrofit fixtures are excluded from the "connected & active" percentage
  // (see CabinetRow.vue) - two extra grouped counts (retrofit total, and the
  // subset of those that are still connected+active) let the caller subtract
  // them from both sides of the ratio without a per-cabinet device scan.
  const [totalsRaw, activeRaw, retrofitRaw, retrofitActiveRaw] = await Promise.all([
    API.streamingPost(Types.COMPANIES, path, buildBody(), {}),
    API.streamingPost(Types.COMPANIES, path, buildBody('sys___active=1 AND sys___connected=1'), {}),
    API.streamingPost(Types.COMPANIES, path, buildBody("pole.operation_state.state='retrofit'"), {}),
    API.streamingPost(Types.COMPANIES, path, buildBody("pole.operation_state.state='retrofit' AND sys___active=1 AND sys___connected=1"), {})
  ]);

  const result: Record<string, FixtureCount> = {};
  const apply = (rows: any, key: keyof FixtureCount) => {
    (Array.isArray(rows) ? rows : []).forEach((row: any) => {
      const cabinet_id = row && row['meta.device.cabinet_id'];
      if (!cabinet_id) return;
      const count = Number(row.count || 0);
      if (!result[cabinet_id]) result[cabinet_id] = { total: 0, connected_active: 0, retrofit: 0, retrofit_connected_active: 0 };
      result[cabinet_id][key] = count;
    });
  };
  apply(totalsRaw, 'total');
  apply(activeRaw, 'connected_active');
  apply(retrofitRaw, 'retrofit');
  apply(retrofitActiveRaw, 'retrofit_connected_active');
  return result;
}
